import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { Calendar, Heart, TrendingUp, Zap } from 'lucide-react'

import { getBmiCategory } from '../ui/bmi-gauge'
import type { AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'

type PreliminaryPathway = 'requires_clinical_review' | 'microdose_support' | 'comorbidity_dependent' | 'standard_titration'

interface BmiEducationTemplateProps {
  stage: WorkflowStage
  bmi: number
  onSelect: (value: AnswerValue) => void
  currentPhase?: PhaseInfo | null
}

interface JourneyMilestone {
  icon: React.ElementType
  title: string
  description: string
  timeframe: string
}

function getPathwayInfo(bmi: number): {
  pathway: PreliminaryPathway
  title: string
  subtitle: string
  showComorbidityNext: boolean
  lifestyleImage: string
  milestones: JourneyMilestone[]
} {
  if (bmi < 20) {
    return {
      pathway: 'requires_clinical_review',
      title: "Let's take a closer look",
      subtitle: 'Your clinician will review your profile to find the right approach for you.',
      showComorbidityNext: false,
      lifestyleImage: '/img/understanding-your-symptoms.webp',
      milestones: [
        { icon: Heart, title: 'Personalized Review', description: 'Your clinician evaluates your full health picture', timeframe: 'Within 24 hours' },
        { icon: Zap, title: 'Tailored Plan', description: 'Get recommendations designed for your unique needs', timeframe: 'Day 1' },
      ],
    }
  }
  if (bmi >= 20 && bmi < 27) {
    return {
      pathway: 'microdose_support',
      title: 'Your path forward',
      subtitle: 'Gentle, clinician-guided support for sustainable metabolic wellness.',
      showComorbidityNext: false,
      lifestyleImage: '/img/not-alone.webp',
      milestones: [
        { icon: Calendar, title: 'Gentle Start', description: 'Low-dose approach tailored to your body', timeframe: 'Week 1' },
        { icon: Zap, title: 'Steady Progress', description: 'Build sustainable habits that last', timeframe: 'Month 1-3' },
        { icon: TrendingUp, title: 'Metabolic Support', description: 'Long-term wellness, not quick fixes', timeframe: 'Ongoing' },
      ],
    }
  }
  if (bmi >= 27 && bmi < 30) {
    return {
      pathway: 'comorbidity_dependent',
      title: 'Understanding your health profile',
      subtitle: 'A few more questions will help your clinician determine the best approach.',
      showComorbidityNext: true,
      lifestyleImage: '/img/women-bg.webp',
      milestones: [
        { icon: Heart, title: 'Health Check', description: "We'll ask about relevant health conditions", timeframe: 'Next screen' },
        { icon: Zap, title: 'Personalized Plan', description: 'Your clinician creates your custom pathway', timeframe: 'Within 24 hours' },
      ],
    }
  }
  return {
    pathway: 'standard_titration',
    title: 'Your journey begins here',
    subtitle: "You're taking the first step toward lasting change. Here's what to expect.",
    showComorbidityNext: false,
    lifestyleImage: '/img/jennie.webp',
    milestones: [
      { icon: Calendar, title: 'Gentle Titration', description: 'Start low, increase gradually for comfort', timeframe: 'Month 1-2' },
      { icon: Zap, title: 'Metabolic Shift', description: 'Notice appetite changes and energy improvements', timeframe: 'Month 2-4' },
      { icon: TrendingUp, title: 'Sustained Results', description: 'Build habits for long-term wellness', timeframe: 'Month 4+' },
      { icon: Heart, title: 'Ongoing Support', description: 'Your clinician adjusts as you progress', timeframe: 'Continuous' },
    ],
  }
}

export default function BmiEducationTemplate({ bmi, onSelect, currentPhase }: BmiEducationTemplateProps) {
  const category = getBmiCategory(bmi)
  const pathwayInfo = useMemo(() => getPathwayInfo(bmi), [bmi])

  const handleContinue = () => {
    onSelect({
      bmi_category: category.category,
      preliminary_pathway: pathwayInfo.pathway,
      show_comorbidity_screen: pathwayInfo.showComorbidityNext,
    })
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }} className="relative mb-6 h-[200px] w-full overflow-hidden rounded-2xl">
        <img src={pathwayInfo.lifestyleImage} alt="Your journey" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-dark)] via-transparent to-transparent" />
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-8 space-y-3">
        {pathwayInfo.milestones.map((milestone, index) => {
          const Icon = milestone.icon
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className="flex gap-4 rounded-xl border border-white/10 bg-white/5 p-4"
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[var(--ww-accent)]/10">
                <Icon className="h-5 w-5 text-[var(--ww-accent)]" />
              </div>
              <div className="flex-1">
                <div className="mb-1 flex items-baseline justify-between">
                  <h4 className="text-sm font-semibold text-white">{milestone.title}</h4>
                  <span className="text-xs font-medium text-[var(--ww-accent)]">{milestone.timeframe}</span>
                </div>
                <p className="text-sm leading-relaxed text-white/60">{milestone.description}</p>
              </div>
            </motion.div>
          )
        })}
      </motion.div>

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)]">
        <button
          onClick={handleContinue}
          className="w-full py-4 px-6 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:bg-[var(--ww-btn-hover)] hover:-translate-y-0.5"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
